import { useState, useEffect } from 'react';
import { useKeycloak } from '@react-keycloak/web';
import { Badge } from "@/components/ui/badge";

// Schema validation metrics interface
interface SchemaValidationMetrics {
  totalValidations: number;
  successCount: number;
  failureCount: number;
  successRate: number;
  recentErrors: ValidationError[];
  schemaBreakdown: SchemaBreakdown[];
  timeSeriesData: ValidationTimePoint[];
}

interface ValidationError {
  id: string;
  timestamp: string;
  schemaName: string;
  jobName: string;
  jobId?: string;
  traceId?: string;
  errorMessage: string;
  errorDetails?: any;
  severity: 'HIGH' | 'MEDIUM' | 'LOW';
}

interface SchemaBreakdown {
  schemaName: string;
  totalAttempts: number;
  successCount: number;
  failureCount: number;
  successRate: number;
  lastFailure?: string;
}

interface ValidationTimePoint {
  timestamp: string;
  successCount: number;
  failureCount: number;
  totalCount: number;
}

const MOCK_VALIDATION_METRICS: SchemaValidationMetrics = {
  totalValidations: 1247,
  successCount: 1189,
  failureCount: 58,
  successRate: 95.35,
  recentErrors: [
    {
      id: '1',
      timestamp: new Date(Date.now() - 3600000).toISOString(),
      schemaName: 'AgentExecutionTraceSchema',
      jobName: 'DocumentUploadIngestJob',
      jobId: 'realm123:doc-456',
      traceId: 'trace-789',
      errorMessage: 'Missing required field: duration_ms',
      severity: 'HIGH'
    },
    {
      id: '2',
      timestamp: new Date(Date.now() - 7200000).toISOString(),
      schemaName: 'McpWorkflowTraceContractSchema',
      jobName: 'CitationExtractionJob',
      jobId: 'realm456:cite-789',
      errorMessage: 'Invalid datetime format in start_timestamp_utc',
      severity: 'MEDIUM'
    },
    {
      id: '3',
      timestamp: new Date(Date.now() - 10800000).toISOString(),
      schemaName: 'FileAgentOutput_DmsFileFocus_v1_0_Schema',
      jobName: 'FileProcessingJob',
      errorMessage: 'UUID validation failed for processed_dms_document_id',
      severity: 'HIGH'
    }
  ],
  schemaBreakdown: [
    { schemaName: 'AgentExecutionTraceSchema', totalAttempts: 423, successCount: 398, failureCount: 25, successRate: 94.09, lastFailure: new Date(Date.now() - 3600000).toISOString() },
    { schemaName: 'McpWorkflowTraceContractSchema', totalAttempts: 287, successCount: 275, failureCount: 12, successRate: 95.82 },
    { schemaName: 'FileAgentOutput_DmsFileFocus_v1_0_Schema', totalAttempts: 198, successCount: 185, failureCount: 13, successRate: 93.43, lastFailure: new Date(Date.now() - 10800000).toISOString() },
    { schemaName: 'EdgeCiteAgent_ConceptualOutput_v0_9_Schema', totalAttempts: 156, successCount: 150, failureCount: 6, successRate: 96.15 },
    { schemaName: 'LLMAgentOutput_Summary_v1_2_Schema', totalAttempts: 183, successCount: 181, failureCount: 2, successRate: 98.91 }
  ],
  timeSeriesData: Array.from({ length: 24 }, (_, i) => ({
    timestamp: new Date(Date.now() - (23 - i) * 3600000).toISOString(),
    successCount: Math.floor(Math.random() * 50) + 20,
    failureCount: Math.floor(Math.random() * 8),
    totalCount: 0
  })).map(point => ({ ...point, totalCount: point.successCount + point.failureCount }))
};

export default function SchemaValidationDashboard() {
  const { keycloak } = useKeycloak();
  const [metrics] = useState<SchemaValidationMetrics>(MOCK_VALIDATION_METRICS);
  const [loading, setLoading] = useState(false);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());
  const [activeTab, setActiveTab] = useState<'overview' | 'errors' | 'schemas' | 'trends'>('overview');

  const refreshMetrics = async () => {
    setLoading(true);
    try {
      // TODO: Replace with actual API call
      // const response = await fetch('/api/jobs/schema-validation-metrics', {
      //   headers: { Authorization: `Bearer ${keycloak.token}` }
      // });
      // const data = await response.json();
      // setMetrics(data);
      
      // Simulate API call delay
      await new Promise(resolve => setTimeout(resolve, 1000));
      setLastRefresh(new Date());
    } catch (error) {
      console.error('Failed to refresh schema validation metrics:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refreshMetrics();
  }, [keycloak.token]);

  const getSeverityColor = (severity: ValidationError['severity']) => {
    switch (severity) {
      case 'HIGH': return 'destructive';
      case 'MEDIUM': return 'secondary';
      case 'LOW': return 'outline';
      default: return 'outline';
    }
  };

  const getSchemaIcon = (schemaName: string) => {
    if (schemaName.includes('Agent')) return '🤖';
    if (schemaName.includes('Workflow') || schemaName.includes('Mcp')) return '🔄';
    if (schemaName.includes('File') || schemaName.includes('Document')) return '📄';
    return '📋';
  };

  return (
    <div className="p-6 bg-white dark:bg-gray-900 text-black dark:text-gray-100 min-h-screen transition-colors">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <span className="text-2xl">🗄️</span>
            Schema Validation Dashboard
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Monitor schema validation health and error patterns across EdgeLex System Monitor
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-500">
            Last updated: {lastRefresh.toLocaleTimeString()}
          </span>
          <button 
            onClick={refreshMetrics} 
            disabled={loading}
            className="px-3 py-1 bg-lex-accent hover:bg-lex-accent-hover disabled:opacity-50 text-lex-accent-contrast rounded text-sm transition-colors flex items-center gap-2"
          >
            <span className={loading ? 'animate-spin' : ''}>🔄</span>
            Refresh
          </button>
        </div>
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Validations</h3>
            <span className="text-2xl">⚡</span>
          </div>
          <div className="text-2xl font-bold text-gray-900 dark:text-white">{metrics.totalValidations.toLocaleString()}</div>
          <p className="text-xs text-gray-500">All-time schema validations</p>
        </div>

        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">Success Rate</h3>
            <span className="text-2xl">📈</span>
          </div>
          <div className="text-2xl font-bold text-green-600">{metrics.successRate.toFixed(2)}%</div>
          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 mt-2">
            <div 
              className="bg-green-600 h-2 rounded-full" 
              style={{ width: `${metrics.successRate}%` }}
            ></div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">Recent Failures</h3>
            <span className="text-2xl">❌</span>
          </div>
          <div className="text-2xl font-bold text-red-600">{metrics.failureCount}</div>
          <p className="text-xs text-gray-500">
            {metrics.recentErrors.length} in last 24h
          </p>
        </div>

        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">Active Schemas</h3>
            <span className="text-2xl">📋</span>
          </div>
          <div className="text-2xl font-bold text-gray-900 dark:text-white">{metrics.schemaBreakdown.length}</div>
          <p className="text-xs text-gray-500">Schema definitions monitored</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700">
        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="flex space-x-8 px-6 py-3">
            {(['overview', 'errors', 'schemas', 'trends'] as const).map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`py-2 px-1 border-b-2 font-medium text-sm capitalize ${
                  activeTab === tab
                    ? 'border-lex-accent text-lex-accent'
                    : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
              >
                {tab}
              </button>
            ))}
          </nav>
        </div>

        <div className="p-6">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Health Status */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                  <span>✅</span>
                  Validation Health
                </h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span>Overall Health</span>
                    <Badge variant={metrics.successRate > 95 ? 'success' : metrics.successRate > 90 ? 'secondary' : 'destructive'}>
                      {metrics.successRate > 95 ? 'Excellent' : metrics.successRate > 90 ? 'Good' : 'Needs Attention'}
                    </Badge>
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>Successful Validations</span>
                      <span className="text-green-600">{metrics.successCount}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Failed Validations</span>
                      <span className="text-red-600">{metrics.failureCount}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Top Problem Schemas */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                  <span>⚠️</span>
                  Problem Schemas
                </h3>
                <div className="space-y-3">
                  {metrics.schemaBreakdown
                    .filter(schema => schema.failureCount > 0)
                    .sort((a, b) => b.failureCount - a.failureCount)
                    .slice(0, 5)
                    .map((schema) => (
                      <div key={schema.schemaName} className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="text-lg">{getSchemaIcon(schema.schemaName)}</span>
                          <div className="text-sm">
                            <div className="font-medium">{schema.schemaName.replace(/Schema$/, '')}</div>
                            <div className="text-gray-500">{schema.failureCount} failures</div>
                          </div>
                        </div>
                        <Badge variant="outline">
                          {schema.successRate.toFixed(1)}%
                        </Badge>
                      </div>
                    ))}
                </div>
              </div>
            </div>
          )}

          {/* Errors Tab */}
          {activeTab === 'errors' && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <span>❌</span>
                Recent Validation Errors
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                Latest schema validation failures requiring attention
              </p>
              <div className="space-y-4">
                {metrics.recentErrors.map((error) => (
                  <div key={error.id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 border-l-4 border-l-red-500">
                    <div className="flex items-start justify-between">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <Badge variant={getSeverityColor(error.severity)}>
                            {error.severity}
                          </Badge>
                          <span className="font-medium">{error.schemaName}</span>
                          <span className="text-sm text-gray-500">in {error.jobName}</span>
                        </div>
                        <div className="text-sm text-red-700 dark:text-red-400">
                          {error.errorMessage}
                        </div>
                        <div className="text-xs text-gray-500">
                          {new Date(error.timestamp).toLocaleString()}
                          {error.jobId && (
                            <span className="ml-2">
                              Job ID: <code className="text-xs bg-gray-100 dark:bg-gray-800 px-1 rounded">{error.jobId}</code>
                            </span>
                          )}
                        </div>
                      </div>
                      {error.jobId && (
                        <button className="px-3 py-1 bg-lex-accent hover:bg-lex-accent-hover text-lex-accent-contrast rounded text-xs">
                          View Job
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Schemas Tab */}
          {activeTab === 'schemas' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {metrics.schemaBreakdown.map((schema) => (
                <div key={schema.schemaName} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                  <div className="mb-3">
                    <h4 className="text-base font-medium text-gray-900 dark:text-white flex items-center gap-2">
                      <span className="text-lg">{getSchemaIcon(schema.schemaName)}</span>
                      {schema.schemaName.replace(/Schema$/, '')}
                    </h4>
                    <p className="text-sm text-gray-500">{schema.totalAttempts} total validations</p>
                  </div>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span>Success Rate</span>
                      <Badge variant={schema.successRate > 95 ? 'success' : schema.successRate > 90 ? 'secondary' : 'destructive'}>
                        {schema.successRate.toFixed(2)}%
                      </Badge>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                      <div 
                        className="bg-green-600 h-2 rounded-full" 
                        style={{ width: `${schema.successRate}%` }}
                      ></div>
                    </div>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <div className="text-green-600 font-medium">{schema.successCount}</div>
                        <div className="text-gray-500">Successful</div>
                      </div>
                      <div>
                        <div className="text-red-600 font-medium">{schema.failureCount}</div>
                        <div className="text-gray-500">Failed</div>
                      </div>
                    </div>
                    {schema.lastFailure && (
                      <div className="text-xs text-gray-500">
                        Last failure: {new Date(schema.lastFailure).toLocaleString()}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Trends Tab */}
          {activeTab === 'trends' && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Validation Trends (24h)</h3>
              <p className="text-gray-600 dark:text-gray-400">
                Hourly validation success and failure patterns
              </p>
              <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
                <div className="h-64 flex items-end justify-between gap-1">
                  {metrics.timeSeriesData.map((point, index) => {
                    const hour = new Date(point.timestamp).getHours();
                    const successHeight = (point.successCount / Math.max(...metrics.timeSeriesData.map(p => p.totalCount))) * 200;
                    const failureHeight = (point.failureCount / Math.max(...metrics.timeSeriesData.map(p => p.totalCount))) * 200;
                    
                    return (
                      <div key={index} className="flex flex-col items-center" style={{ height: '240px' }}>
                        <div className="flex flex-col justify-end" style={{ height: '200px' }}>
                          {point.failureCount > 0 && (
                            <div 
                              className="w-3 bg-red-400 rounded-t-sm"
                              style={{ height: `${failureHeight}px` }}
                              title={`${point.failureCount} failures at ${hour}:00`}
                            />
                          )}
                          <div 
                            className={`w-3 bg-green-400 ${point.failureCount === 0 ? 'rounded-t-sm' : ''}`}
                            style={{ height: `${successHeight}px` }}
                            title={`${point.successCount} successes at ${hour}:00`}
                          />
                        </div>
                        <div className="text-xs text-gray-500 mt-2">
                          {hour}h
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="flex items-center justify-center gap-4 mt-4 text-sm">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-green-400 rounded"></div>
                    <span>Successful</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-red-400 rounded"></div>
                    <span>Failed</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
} 